import { useRef } from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router';

import { type Permission, useHasPermissions } from '~domains/auth';

import { ROUTES } from '~shared/config/routes';
import { useOnMountEffect } from '~shared/hooks/use-on-mount-effect';

function isTransactionsRoute(route: string) {
  return route === ROUTES.transactions.root;
}

const TAB_KEY = 'lastTransactionsTab';
const DEFAULT_TAB = 'loans';

type TransactionTabs = 'loans' | 'payments';

const mapTabToPermission: Record<TransactionTabs, Permission> = {
  loans: 'APPLICATIONS_VIEW',
  payments: 'TRANSACTIONS_VIEW',
};

const TAB_ORDER = Object.keys(mapTabToPermission) as TransactionTabs[];

function isTransactionTab(tab?: string): tab is TransactionTabs {
  return tab === 'loans' || tab === 'payments';
}

export function useLastVisitedTab() {
  const lastTabRef = useRef<TransactionTabs>(
    (localStorage.getItem(TAB_KEY) as TransactionTabs) ?? DEFAULT_TAB
  );
  const location = useLocation();
  const navigate = useNavigate();
  const currentTabMatch = useMatch(`${ROUTES.transactions.root}/:tab`);
  const { checkPermissions } = useHasPermissions();

  const canViewTab = (tab: TransactionTabs) =>
    checkPermissions([mapTabToPermission[tab]]);

  const shouldRedirect = isTransactionsRoute(location.pathname);

  /**
   * we should redirect from "/transactions" to the last visited tab, falling
   * back to the first permitted one when it is no longer accessible
   */
  useOnMountEffect(() => {
    if (!shouldRedirect) return;

    const targetTab = canViewTab(lastTabRef.current)
      ? lastTabRef.current
      : TAB_ORDER.find(canViewTab);

    if (!targetTab) {
      // no tab is accessible anymore — drop the stale preference
      lastTabRef.current = DEFAULT_TAB;
      localStorage.removeItem(TAB_KEY);
      return;
    }

    // the last visited tab is no longer accessible — sync the fallback
    if (targetTab !== lastTabRef.current) {
      lastTabRef.current = targetTab;
      localStorage.setItem(TAB_KEY, targetTab);
    }

    navigate(targetTab, { replace: true });
  });

  /**
   * we should track and save to localStorage last visited tab
   */
  if (
    !shouldRedirect &&
    isTransactionTab(currentTabMatch?.params.tab) &&
    currentTabMatch.params.tab !== lastTabRef.current
  ) {
    lastTabRef.current = currentTabMatch.params.tab;
    localStorage.setItem(TAB_KEY, lastTabRef.current);
  }
}
